import { useMemo, useState, type ReactNode } from 'react';
import type { ReferenceKind } from '@cercle/shared';
import { Input, Muted, Tag } from '../ui';
import { useReference } from './useReference';
import styles from './reference.module.scss';

/**
 * Saisir un terme du référentiel, ou en créer un.
 *
 * Il corrige une asymétrie que le scaffold traînait : le pays était libre mais sans
 * aucune suggestion, tandis que le style était suggéré mais sans liberté — deux colonnes
 * pourtant identiques en base. Ici les deux se comportent pareil.
 *
 * Le principe : on tape, on voit ce qui existe, et si rien ne correspond une entrée
 * « Ajouter « Grogue » » apparaît. C'est le geste que le lot devait rendre possible —
 * ne jamais laisser un membre bloqué en séance parce qu'une valeur manquait.
 */
export interface ReferenceComboProps {
  kind: ReferenceKind;
  value: string;
  onChange: (label: string) => void;
  placeholder?: string;
  /** Pour un descripteur : la famille dans laquelle créer le terme. */
  family?: string;
  /** Rend le champ vide équivalent à « aucune valeur ». */
  allowEmpty?: boolean;
}

export function ReferenceCombo({
  kind,
  value,
  onChange,
  placeholder,
  family,
  allowEmpty = true,
}: ReferenceComboProps): ReactNode {
  const { terms, add } = useReference(kind);
  const [query, setQuery] = useState(value);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const needle = normaliseForSearch(query);

  const matches = useMemo(
    () =>
      needle
        ? terms.filter((term) => normaliseForSearch(term.label).includes(needle)).slice(0, 8)
        : // Rien de saisi : on montre les plus employés, qui sont déjà en tête de liste.
          terms.slice(0, 8),
    [terms, needle],
  );

  const exact = terms.some((term) => normaliseForSearch(term.label) === needle);
  const canCreate = needle.length >= 2 && !exact;

  async function create(): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      const term = await add(query.trim(), family ?? null);
      setQuery(term.label);
      onChange(term.label);
    } catch {
      // L'ajout exige d'être en ligne : deux appareils déconnectés créeraient deux termes
      // que la normalisation ne pourrait plus réconcilier.
      setError('Impossible d’ajouter ce terme hors-ligne. Choisissez-en un existant.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className={styles.combo}>
      <Input
        value={query}
        placeholder={placeholder}
        onChange={(event) => {
          setQuery(event.target.value);
          setError(null);
          // Une frappe qui tombe juste vaut sélection : on n'oblige pas à cliquer.
          const hit = terms.find(
            (term) => normaliseForSearch(term.label) === normaliseForSearch(event.target.value),
          );
          if (hit) onChange(hit.label);
          else if (allowEmpty && event.target.value.trim() === '') onChange('');
        }}
      />

      <div className={styles.suggestions}>
        {matches.map((term) => (
          <Tag
            key={term.id}
            selected={term.label === value}
            onClick={() => {
              setQuery(term.label);
              onChange(term.label === value && allowEmpty ? '' : term.label);
            }}
          >
            {term.label}
          </Tag>
        ))}

        {canCreate ? (
          <Tag dashed disabled={busy} onClick={() => void create()}>
            {busy ? 'Ajout…' : `Ajouter « ${query.trim()} »`}
          </Tag>
        ) : null}
      </div>

      {error ? <Muted>{error}</Muted> : null}
    </div>
  );
}

/** Même intention que la normalisation de l'API, réduite à ce qu'exige une recherche. */
function normaliseForSearch(value: string): string {
  return value
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();
}
